/**
 * Client feedback (2026-09) — "I wanted this in card view, separate cards for
 * separate legends."
 *
 * The stacked chart carries all four series behind a legend, so reading the
 * total for any one status meant eyeballing stack segments across every bar.
 * This renders one card per legend entry — Submitted / Checked / Approved /
 * Rejected — each showing that status's total across the selected window, in
 * the same colour the chart uses for it. The chart stays below as the trend
 * view; these answer "how many, in total".
 *
 * Deliberately NOT lazy-loaded. These cards carry no recharts dependency, so
 * they stay synchronous imports like the other non-chart widgets and render
 * immediately while the chart chunk is still downloading (D-240).
 *
 * Totals are summed client-side from the same `ThroughputBlock.days` the chart
 * consumes, so the numbers cannot disagree with the bars and no API change was
 * needed.
 */

import type { JSX } from 'react';

import { WidgetCard } from '@/components/dashboard/widget-states';
import { Skeleton } from '@/components/ui/skeleton';
import type { ThroughputBlock, ThroughputDay } from '@/types/dashboard';

/**
 * Legend order and colours mirror the stack order in
 * `components/dashboard/charts/StackedBar.tsx` so a card and its chart segment
 * are visually the same series. Keep the two in sync.
 */
const SERIES = [
  { key: 'submitted', label: 'Submitted', color: 'var(--chart-5)' },
  { key: 'checked', label: 'Checked', color: 'var(--chart-1)' },
  { key: 'approved', label: 'Approved', color: 'var(--chart-2)' },
  { key: 'rejected', label: 'Rejected', color: 'var(--chart-4)' },
] as const satisfies readonly {
  key: keyof Omit<ThroughputDay, 'day'>;
  label: string;
  color: string;
}[];

export interface ThroughputStatCardsProps {
  readonly data: ThroughputBlock | null | undefined;
  /** Selected window in days — shown as the per-card subtitle. */
  readonly days: number;
  readonly isLoading: boolean;
  /** Grid span applied to EACH card (the component renders four siblings). */
  readonly className?: string;
}

function total(data: ThroughputBlock | null | undefined, key: keyof Omit<ThroughputDay, 'day'>): number {
  if (!data) return 0;
  return data.days.reduce((sum, d) => sum + d[key], 0);
}

export function ThroughputStatCards({
  data,
  days,
  isLoading,
  className,
}: ThroughputStatCardsProps): JSX.Element {
  return (
    <>
      {SERIES.map((s) => (
        <WidgetCard
          key={s.key}
          title={s.label}
          testId={`throughput-stat-${s.key}`}
          {...(className !== undefined ? { className } : {})}
        >
          {isLoading ? (
            <Skeleton className="h-9 w-16" data-testid={`throughput-stat-${s.key}-skeleton`} />
          ) : (
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="size-3 shrink-0 rounded-sm"
                style={{ backgroundColor: s.color }}
              />
              <span
                className="text-3xl font-semibold tabular-nums"
                data-testid={`throughput-stat-${s.key}-value`}
              >
                {total(data, s.key)}
              </span>
            </div>
          )}
          <p className="text-muted-foreground mt-1 text-xs">last {days} days</p>
        </WidgetCard>
      ))}
    </>
  );
}

export default ThroughputStatCards;
